import React, { useMemo, useState } from "react";
import { Form, Formik } from "formik";
import Bugsnag from "@bugsnag/js";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../ui-elements/Modal/Modal";
import Button from "../../../ui-elements/Button/Button";
import Checkbox from "../../../ui-elements/Input/Formik/Checkbox";
import Alert, { AlertType } from "../../../ui-elements/Alert/Alert";
import Link from "../../../ui-elements/Link/Link";
import { insertDash } from "../../../services/utils";
import {
  CompanyWatchItem,
  AdilarRole,
  ALL_ADILAR_ROLES,
  ADILAR_ROLE_LABELS,
} from "../../../types/CompanyWatchTypes";
import { updateCompanyWatch, removeCompaniesFromWatchList } from "../CompanyWatchHelpers/CompanyWatchApi";

interface IProps {
  show: boolean;
  close(): void;
  data?: CompanyWatchItem;
  setToastMessage(message: string): void;
  setToastType(type: AlertType): void;
  setToastShow(show: boolean): void;
  onSuccess(): void;
}

interface IFormValues {
  pep: boolean;
  sanctions: boolean;
  stjorn: boolean;
  prokuruhafi: boolean;
  framkvaemdastjorn: boolean;
  raunverulegir_eigendur: boolean;
}

const SubFormComponent: React.FC<{
  values: IFormValues;
  data: CompanyWatchItem;
  close(): void;
  onDelete(): void;
}> = ({ values, data, close, onDelete }) => {
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);

  const handleDelete = async (companyId: string) => {
    try {
      const responseBody = await removeCompaniesFromWatchList([companyId]);
      if (responseBody.success) {
        onDelete();
        close();
      }
    } catch (e) {
      if (e instanceof Error) Bugsnag.notify(e);
    }
  };

  return (
    <>
      <Form>
        <div className="KCL_pepEditWatchModal">
          <div className="left_wrapper">
            <span>
              <strong>Lögaðili: </strong>
              {data.name}
            </span>
            <span>
              <strong>Kennitala: </strong>
              {insertDash(data.id.companyId)}
            </span>
          </div>

          <div className="right_wrapper">
            <div className="checkbox-wrapper">
              <Checkbox
                label="Vöktun á PEP lista"
                name="pep"
                id="pep"
                value={values.pep.toString()}
              />
              <Checkbox
                label="Vöktun á alþjóðlegum válistum"
                name="sanctions"
                id="sanctions"
                value={values.sanctions.toString()}
              />
            </div>
            <div>
              <p className="paragraph--bold">Aðilar í vöktun</p>
              <div className="checkbox-wrapper">
                {ALL_ADILAR_ROLES.map((role) => (
                  <Checkbox
                    key={role}
                    label={ADILAR_ROLE_LABELS[role]}
                    name={role}
                    id={`edit-${role}`}
                    value={(values[role] as boolean).toString()}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="form__actions">
          <Button type="button" buttonType="secondary" size="lg" onClick={close}>
            Hætta við
          </Button>
          <Button type="submit" buttonType="primary" size="lg">
            Staðfesta
          </Button>
          <Button
            type="button"
            buttonType="secondary"
            size="lg"
            onClick={() => setConfirmDeleteModalOpen(true)}
            icon={faTrashCan}
          >
            Eyða af lista
          </Button>
        </div>
      </Form>
      <Modal
        className="confirm-delete-modal"
        show={confirmDeleteModalOpen}
        onHide={() => setConfirmDeleteModalOpen(false)}
        header="Eyða lögaðila"
        body={
          <div className="alertBody">
            <Alert
              type="alert"
              headText="Þú ert að fara eyða lögaðila úr vöktunarkerfinu."
            />
            <div className="infoSection">
              <p className="paragraph">
                Allar tengdar upplýsingar verða{" "}
                <span className="paragraph--bold">endanlega fjarlægðar</span> og ekki
                verður unnt að endurheimta þær.
              </p>
              <p className="paragraph">Viltu staðfesta?</p>
            </div>
            <div className="actions">
              <Button
                onClick={() => setConfirmDeleteModalOpen(false)}
                buttonType="secondary"
                size="lg"
              >
                Hætta við
              </Button>
              <Button
                onClick={() => handleDelete(data.id.companyId)}
                buttonType="primary"
                size="lg"
              >
                Staðfesta
              </Button>
            </div>
          </div>
        }
      />
    </>
  );
};

const CompanyWatchEditModal: React.FC<IProps> = ({
  show,
  close,
  data,
  setToastMessage,
  setToastType,
  setToastShow,
  onSuccess,
}) => {
  const subFormComponent = useMemo(() => {
    return (props: { values: IFormValues }) => {
      if (!data) return null;
      return <SubFormComponent values={props.values} data={data} close={close} onDelete={onSuccess} />;
    };
  }, [close, data, onSuccess]);

  const handleSubmit = async (values: IFormValues) => {
    if (!data) return;

    try {
      const adilarIVoktun: AdilarRole[] = ALL_ADILAR_ROLES.filter(
        (role) => values[role]
      );

      const responseBody = await updateCompanyWatch(
        data.id.companyId,
        values.pep ? "daily" : "never",
        values.sanctions ? "daily" : "never",
        adilarIVoktun
      );

      if (responseBody.success) {
        onSuccess();
        setToastMessage("Vöktun á lögaðila breytt");
        setToastType("success");
        setToastShow(true);
        close();
      } else {
        setToastMessage("Vöktun á lögaðila ekki breytt");
        setToastType("alert");
        setToastShow(true);
      }
    } catch (e) {
      if (e instanceof Error) Bugsnag.notify(e);
      setToastMessage("Vöktun á lögaðila ekki breytt");
      setToastType("error");
      setToastShow(true);
    }
  };

  const modalBody = () => {
    if (!data) return null;

    const adilarSet = new Set(data.adilarList);

    return (
      <div>
        <h3>Breyta vöktun á lögaðila</h3>
        <p className="paragraph--bold">
          Verð eru samkvæmt{" "}
          <Link linkSize="18" url="/Verdskra#Gagnaleit">
            verðskrá Keldunnar
          </Link>{" "}
          eða samningi hverju sinni.
        </p>
        <Formik
          initialValues={{
            pep: data.pepFrequency !== "never",
            sanctions: data.sanctionsFrequency !== "never",
            stjorn: adilarSet.has("stjorn"),
            prokuruhafi: adilarSet.has("prokuruhafi"),
            framkvaemdastjorn: adilarSet.has("framkvaemdastjorn"),
            raunverulegir_eigendur: adilarSet.has("raunverulegir_eigendur"),
          }}
          onSubmit={handleSubmit}
          component={subFormComponent}
        />
      </div>
    );
  };

  return <Modal className="pep-watch-modal" show={show} onHide={close} hasCloseButton body={modalBody()} />;
};

export default CompanyWatchEditModal;
